import { monthRange, fetchPaged } from "./shared";

const invoiceResource = (ctx) =>
  ctx?.ServerSetting?.contractType === 2 ? "leaseinvoices" : "invoices";

const invoiceAmount = (item) =>
  Number(
    item?.saleNetAmount ??
      item?.salenetamount ??
      item?.saleTotalAmount ??
      item?.amount ??
      0
  ) || 0;

const invoiceBalanceAmount = (item) =>
  Number(
    item?.invoiceBalance ??
      item?.invoicebalance ??
      item?.remainingBalance ??
      item?.remainingAmount ??
      0
  ) || 0;

const invoicePaidAmount = (item) =>
  Math.max(0, invoiceAmount(item) - invoiceBalanceAmount(item));

const invoiceDate = (item) =>
  item?.invoiceDate || item?.serviceDate || item?.createdTime;

export async function fetchInvoicesLive(ctx, { from, to, limit, maxPages } = {}) {
  const resource = invoiceResource(ctx);
  return fetchPaged(
    (page, pageSize) => {
      const params = [`page=${page}`, `limit=${pageSize}`];
      if (from) params.push(`from=${encodeURIComponent(from)}`);
      if (to) params.push(`to=${encodeURIComponent(to)}`);
      return `/api/${resource}/get?${params.join("&")}`;
    },
    { limit: limit || 50, maxPages: maxPages || 6 }
  );
}

export async function fetchPendingInvoicesLive(ctx) {
  const now = new Date();
  now.setHours(23, 59, 59, 999);
  const rows = await fetchInvoicesLive(ctx, {
    from: new Date(2025, 0, 1).toISOString(),
    to: now.toISOString(),
    limit: 1000,
    maxPages: 1,
  });
  return rows.filter((item) => invoiceBalanceAmount(item) > 1);
}

const summarizeInvoices = (rows = []) => {
  const totalAmount = rows.reduce((sum, item) => sum + invoiceAmount(item), 0);
  const pendingAmount = rows.reduce(
    (sum, item) => sum + invoiceBalanceAmount(item),
    0
  );
  const paidAmount = rows.reduce((sum, item) => sum + invoicePaidAmount(item), 0);

  return {
    totalInvoices: rows.length,
    totalAmount,
    pendingAmount,
    paidAmount,
  };
};

const mapInvoice = (item) => ({
  id: item.id || item.invoiceNumber || item.invoiceNo,
  invoiceNumber: item.invoiceNumber || item.invoiceNo || item.id,
  customer: item.attributes?.c_accountName || item.accountName || "",
  date: invoiceDate(item),
  totalAmount: invoiceAmount(item),
  paidAmount: invoicePaidAmount(item),
  pendingAmount: invoiceBalanceAmount(item),
});

export async function loadInvoicesQuestion(questionId, ctx) {
  switch (questionId) {
    case "invoicesCurrentMonth":
    case "invoicesLastMonth": {
      const current = monthRange(0);
      const last = monthRange(-1);
      const [currentRows, lastRows] = await Promise.all([
        fetchInvoicesLive(ctx, current),
        fetchInvoicesLive(ctx, last),
      ]);
      return {
        summary: {
          currentMonth: {
            label: current.label,
            items: currentRows.map(mapInvoice),
            ...summarizeInvoices(currentRows),
          },
          lastMonth: {
            label: last.label,
            items: lastRows.map(mapInvoice),
            ...summarizeInvoices(lastRows),
          },
        },
      };
    }

    case "invoiceSummary": {
      const now = new Date();
      now.setHours(23, 59, 59, 999);
      const from = new Date(2025, 0, 1).toISOString();
      const to = now.toISOString();
      const rows = await fetchInvoicesLive(ctx, {
        from,
        to,
        limit: 1000,
        maxPages: 1,
      });
      return {
        summary: {
          label: "2025",
          from,
          to,
          ...summarizeInvoices(rows),
        },
      };
    }

    case "pendingInvoices": {
      const unpaid = await fetchPendingInvoicesLive(ctx);
      return {
        list: unpaid.map(mapInvoice),
        summary: summarizeInvoices(unpaid),
      };
    }

    default:
      return null;
  }
}
